import API from '@/api';
import get from 'lodash/get';
import { mapGetters } from 'vuex';
import axios from 'axios';
import moment from 'moment';

export default {
  data() {
    return {
      statuses: [
        {
          value: 'sending',
          text: 'Sending',
          icon: 'email',
          class: 'grey--text',
        },
        {
          value: 'sent',
          text: 'Sent',
          icon: 'email',
          class: 'primary--text',
        },
        {
          value: 'signed',
          text: 'Signed',
          icon: 'check_circle',
          class: 'success--text',
        },
        {
          value: 'finished',
          text: 'Finished',
          icon: 'task_alt',
          class: 'success--text',
        },
        {
          value: 'error',
          text: 'Error',
          icon: 'cancel',
          class: 'error--text',
        },
        {
          value: 'rejected',
          text: 'Rejected',
          icon: 'cancel',
          class: 'error--text',
        },
        {
          value: 'processing',
          text: 'Processing',
          icon: 'circle',
          class: 'grey--text',
        },
        {
          value: 'importing',
          text: 'Import in progress',
          icon: 'computer',
          class: 'grey--text',
        },
        {
          value: 'action_required',
          text: 'Action required',
          icon: 'warning',
          class: 'warning--text',
        },
        {
          value: 'paid',
          text: 'Paid',
          icon: 'check',
          class: 'success--text',
        },
        {
          value: 'invoice_paid',
          text: 'Invoice paid',
          icon: 'paid',
          class: 'success--text',
        },
      ],

      formDefinitions: [],
      voucherDefinitions: [],
    };
  },

  computed: {
    ...mapGetters({
      currentUser: 'user',
    }),

    taxReturnsMapped() {
      return this.taxReturns.slice().map(taxReturn => {
        return {
          ...taxReturn,
          sorted_payment_vouchers: taxReturn.payment_vouchers.sort(
            this.sortPaymentsByDueDate,
          ),
          completed_signature_count: this.getCompletedSignatureCount(taxReturn),
          total_signature_count: this.getTotalSignatureCount(taxReturn),
          remaining_signature_count: this.getRemainingSignatureCount(taxReturn),
          completed_payment_count: this.getCompletedPaymentCount(taxReturn),
          total_payment_count: this.getTotalPaymentCount(taxReturn),
          remaining_payment_count: this.getRemainingPaymentCount(taxReturn),
          tax_return_status: this.getTaxReturnStatus(taxReturn),
        };
      });
    },
  },

  methods: {
    viewTaxReturn(item) {
      if (item.status === 'processing') {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'The tax return is still being processed. Please wait.',
        });
        return;
      } else if (item.status === 'error') {
        this.$store.dispatch('application/setErrorNotification', {
          message:
            'The tax return has an error during processing. Please contact support.',
        });
        return;
      }

      this.currentTaxReturnId = item.id;
      this.reviewTaxReturnDialogOpened = true;
    },

    async toggleReminder(taxReturn, enabled) {
      taxReturn.reminder_enabled = enabled;

      await API.taxReturn.updateTaxReturn({
        id: taxReturn.id,
        companyId: taxReturn.company_id,
        reminder_disabled: !enabled,
      });
    },

    getTaxReturnStatus(taxReturn) {
      let currentStatus = taxReturn.status;

      if (taxReturn.payment && taxReturn.payment.status === 'paid') {
        currentStatus = 'invoice_paid';
      } else if (
        taxReturn.e_sign_forms.length > 0 &&
        taxReturn.all_signed &&
        taxReturn.status !== 'finished'
      ) {
        currentStatus = 'signed';
      }

      return this.statuses.find(s => s.value === currentStatus);
    },

    getCompletedSignatureCount(taxReturn) {
      return taxReturn.e_sign_forms.reduce((acc, form) => {
        const signedCount = form.signatures.filter(signature => {
          if (form.wetSignaturesRequired) {
            return form.wetSignatures && form.wetSignatures.length > 0;
          } else {
            return signature.signatureData;
          }
        }).length;

        return acc + signedCount;
      }, 0);
    },

    getTotalSignatureCount(taxReturn) {
      return taxReturn.e_sign_forms.reduce((acc, form) => {
        return acc + form.signatures.length;
      }, 0);
    },

    getRemainingSignatureCount(taxReturn) {
      return (
        this.getTotalSignatureCount(taxReturn) -
        this.getCompletedSignatureCount(taxReturn)
      );
    },

    getCompletedPaymentCount(taxReturn) {
      const isInvoicePaid =
        taxReturn.payment && taxReturn.payment.status === 'paid';

      return (
        taxReturn.payment_vouchers.filter(voucher => {
          return voucher.is_paid;
        }).length + (isInvoicePaid ? 1 : 0)
      );
    },

    getTotalPaymentCount(taxReturn) {
      return taxReturn.payment_vouchers.length + (taxReturn.payment ? 1 : 0);
    },

    getRemainingPaymentCount(taxReturn) {
      return (
        this.getTotalPaymentCount(taxReturn) -
        this.getCompletedPaymentCount(taxReturn)
      );
    },

    sortPaymentsByDueDate(a, b) {
      if (a.is_due_asap) {
        return -1;
      } else if (b.is_due_asap) {
        return 1;
      } else if (!a.due_date) {
        return -1;
      } else if (!b.due_date) {
        return 1;
      } else {
        return new Date(a.due_date) - new Date(b.due_date);
      }
    },

    printDueDate(voucher) {
      if (voucher.is_due_asap) {
        return 'ASAP';
      } else if (!voucher.due_date) {
        return 'Not specified';
      } else {
        return moment(voucher.due_date).format('MMM D, YYYY');
      }
    },

    async getFormDefinitions() {
      const response = await API.signatureRequest.getFormDefinitions();

      this.formDefinitions = response.data.concat({
        id: 'other',
        name: 'Other (custom)',
        taxpayer_signature: null,
        spouse_signature: null,
      });
    },

    getFormDefinitionById(id) {
      return this.formDefinitions.find(form => form.id === id);
    },

    getFormNameById(id) {
      return get(this.getFormDefinitionById(id), 'name', 'Other');
    },

    async getVoucherDefinitions() {
      const response = await axios.get(`/tax_returns/get_voucher_definitions`);
      this.voucherDefinitions = response.data;
    },

    getVoucherDefinitionById(id) {
      return this.voucherDefinitions.find(voucher => voucher.id === id);
    },

    getVoucherNameById(id) {
      return get(this.getVoucherDefinitionById(id), 'name', 'Unknown');
    },

    async remove(item) {
      await axios.delete('/tax_returns/' + item.id, {
        data: { company_id: item.company_id },
      });
      await this.refreshTable();
    },

    async sendReminders(item) {
      await axios.post('/tax_returns/' + item.id + '/send_reminders', {
        company_id: item.company_id,
      });
      await this.refreshTable();
    },

    async markAsFinished(item) {
      await axios.patch('/tax_returns/' + item.id, {
        company_id: item.company_id,
        status: 'finished',
      });
      await this.refreshTable();
    },

    async resend(item) {
      await axios.post('/tax_returns/' + item.id + '/resend', {
        company_id: item.company_id,
      });
      await this.refreshTable();
    },

    getName(item) {
      if (item.tax_return_name) {
        return item.tax_return_name;
      } else {
        return this.getDocumentName(item.original_file);
      }
    },

    async downloadDocument(item) {
      try {
        const source = item.signed_document
          ? item.signed_document
          : item.source;

        const response1 = await API.integration.downloadDocument({
          companyId: item.company_id,
          link: source,
        });

        const name = this.getDocumentName(source);

        this.downloadDocumentByLink(response1.data.document.data.link, name);
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem downloading the document.',
        });
      }
    },

    async downloadSignedPagesOnly(item) {
      try {
        const source = item.signed_pages_only;

        const response1 = await API.integration.downloadDocument({
          companyId: item.company_id,
          link: source,
        });

        const name = this.getDocumentName(source);

        this.downloadDocumentByLink(response1.data.document.data.link, name);
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem downloading the document.',
        });
      }
    },

    getDocumentName(document) {
      return document.split('/')[document.split('/').length - 1];
    },

    async downloadDocumentByLink(url, name) {
      let response = await this.$http.get(url, { responseType: 'arraybuffer' });
      let blob = new Blob([response.data], {
        type: get(response.headers, 'content-type'),
      });
      let link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = name;
      link.click();
    },
  },
};
